import React from 'react';
import {unwrapResult} from '@reduxjs/toolkit';
import {useDispatch, useSelector} from 'react-redux';
import {useHistory} from 'react-router-dom';
import {
  Col,
  UiBox,
  UiFormControl,
  UiFormErrorMessage,
  UiImage,
  UiQuestionnaireLayout,
  UiSimpleGrid,
  UiText,
  UiTextarea,
} from '~/components/elements';
import {ScrollToBottomButton} from '~/components/shared/ScrollToBottomButton';
import {ACNE_TELECONSULTATION_KEYS, METHOD_KEYS, NO_OPTION, YES_OPTION} from '~/constants/medical-questionnaires';
import {useInput, useRequestState} from '~/hooks';
import {setQuestionnaireAnswer, setUserQuestionnaireId} from '~/redux/questionnaires/slice';
import {addQuestionnaire} from '~/redux/questionnaires/thunk';
import {RootState} from '~/redux/root-reducer';
import {AppDispatch} from '~/redux/root-store';
import {getCurrentUserFromStorage} from '~/services/auth';
import {spacing} from '~/utils/theme/spacing';
import {requestOK} from '~/utils/thunk-api';

const Questionnaire11Screen = () => {
  const dispatch: AppDispatch = useDispatch();
  const history = useHistory();
  const {accessToken: savedToken} = getCurrentUserFromStorage();
  const medicalQuestionnaire = useSelector((state: RootState) => state.questionnaires.medicalQuestionnaire);
  const {wpAuthUser} = medicalQuestionnaire;
  const questionnaires = medicalQuestionnaire?.questionnaires;
  const questionnaire = questionnaires?.find(
    i => i.key === ACNE_TELECONSULTATION_KEYS.OTHER_CONDITION_OR_MEDICAL_HISTORY,
  );
  const medicalQuestionnaireAnswer = useSelector((state: RootState) => state.questionnaires?.collectData);

  const currentAnswer = useSelector(
    (state: RootState) =>
      state.questionnaires?.collectData[ACNE_TELECONSULTATION_KEYS.OTHER_CONDITION_OR_MEDICAL_HISTORY]?.data,
  );

  const {loading} = useRequestState(addQuestionnaire);

  const rules = {
    medicationsTaking: (attributes: any) => ({
      presence: {allowEmpty: false, message: `^This field can't be blank`},
      length: {
        minimum: 5,
        maximum: 1000,
        tooShort: '^This field is too short (minimum is %{count} characters)',
        tooLong: '^This field is too long (maximum is %{count} characters)',
        tokenizer: function (value: any) {
          return trimSpaces(value);
        },
      },
    }),
  };

  const {inputs, setInputs, validation, trimSpaces} = useInput(
    {
      selectedOption: currentAnswer?.selectedOption || '',
      medicationsTaking: currentAnswer?.medicationsTaking || '',
    },
    {rules},
  );

  const onClickOption = (value: string) => {
    if (value === NO_OPTION) {
      setInputs({selectedOption: value, medicationsTaking: ''});
    }
    setInputs({selectedOption: value});
  };

  const onClickSubmit = async () => {
    if (inputs.selectedOption === YES_OPTION) {
      const errors = validation.getErrors();
      if (errors) {
        return;
      }
    }
    const questionnaireAnswer = {
      key: ACNE_TELECONSULTATION_KEYS.OTHER_CONDITION_OR_MEDICAL_HISTORY,
      index: questionnaire?.index || '',
      data: {
        selectedOption: inputs.selectedOption || '',
        medicationsTaking: inputs.medicationsTaking.trim() || '',
      },
    };
    dispatch(setQuestionnaireAnswer(questionnaireAnswer));
    const resAction = await dispatch(
      addQuestionnaire({
        data: {
          data: {
            ...medicalQuestionnaireAnswer,
            [ACNE_TELECONSULTATION_KEYS.OTHER_CONDITION_OR_MEDICAL_HISTORY]: {...questionnaireAnswer},
          },
          wpUserId: wpAuthUser?.wpUserId,
          methodKey: METHOD_KEYS.ACNE_TELECONSULTATION,
        },
      }),
    );
    if (requestOK(resAction, addQuestionnaire)) {
      const result = unwrapResult(resAction);
      if (result?.booking) {
        const type = result?.slotTime;
        dispatch(setUserQuestionnaireId({id: result?.medical?.id}));
        history.push(`/appointment?type=${type}&methodKey=${METHOD_KEYS.ACNE_TELECONSULTATION}&token=${savedToken}`);
      }
    }
  };

  const renderTellMore = () => {
    if (inputs.selectedOption === YES_OPTION) {
      const selectedOptionData = questionnaire?.options?.find(i => i.value === inputs.selectedOption);
      return (
        <UiBox w="100%" mt={spacing.small}>
          {selectedOptionData?.relations?.map(i => {
            if (i?.elementType?.name === 'inputArea') {
              return (
                <UiBox
                  w="100%"
                  mt={spacing.small}
                  key={i?.key || i?.elementType?.name}
                  className="animate__animated animate__fadeInLeft"
                >
                  <UiText fontSize="md" fontWeight="600" mt={spacing.small}>
                    {i?.questions?.default}
                  </UiText>
                  <UiFormControl id="medicationsTaking" isRequired isInvalid={!!validation?.errors?.medicationsTaking}>
                    <UiTextarea
                      mt={spacing.small}
                      bg="white"
                      borderRadius={spacing.tiny}
                      placeholder={i?.elementType?.placeholder}
                      value={inputs.medicationsTaking}
                      onChange={({target}) => setInputs({medicationsTaking: target.value})}
                      minH="200px"
                    />
                    {validation?.errors?.medicationsTaking && (
                      <UiFormErrorMessage>{validation?.errors?.medicationsTaking[0]}</UiFormErrorMessage>
                    )}
                  </UiFormControl>
                </UiBox>
              );
            }
            return null;
          })}
        </UiBox>
      );
    } else if (inputs.selectedOption === NO_OPTION) {
      return (
        <UiBox mt={spacing.small} className="animate__animated animate__fadeInLeft" w="100%">
          {questionnaire?.descriptions?.default?.map(val => (
            <UiText key={val} mt={spacing.small} textColor="liteOuter">
              {val}
            </UiText>
          ))}
        </UiBox>
      );
    }
    return null;
  };

  const valid =
    inputs.selectedOption === YES_OPTION ? Object.values(inputs).every(el => !!el) : !!inputs.selectedOption;

  return (
    <UiQuestionnaireLayout
      actions={questionnaire?.actions}
      disableNext={!valid}
      handleOnNext={onClickSubmit}
      loading={loading}
      checkoutStep={questionnaire?.checkoutStep}
    >
      <Col alignItems="center" textAlign="center">
        <UiImage type="icon" name={questionnaire?.icon} boxSize={spacing.mLarge} my={spacing.small} />
        <UiText fontSize="xl" fontWeight="bold" mt={spacing.medium}>
          {questionnaire?.questions?.default}
        </UiText>
        <UiBox mt={spacing.small} w="100%">
          <UiSimpleGrid columns={2} spacing={5}>
            {questionnaire?.options?.map(value => (
              <ScrollToBottomButton
                key={`${value.label}-${value.value}`}
                title={value.label}
                variant={inputs.selectedOption === value.value ? 'solid' : 'toggle'}
                onClick={() => onClickOption(value.value as string)}
              />
            ))}
          </UiSimpleGrid>
        </UiBox>
        {renderTellMore()}
      </Col>
    </UiQuestionnaireLayout>
  );
};

export default Questionnaire11Screen;
